import type {
  Kmat,
  LinearConfiguration,
  LinearConfigurationUpdate,
  LinearStep,
  LinearSubstep,
  SubstepTile,
} from "~/Configurators/Linear/models";
import { type ConfiguratorType } from "~/Configurators/ConfiguratorType";
import type {
  ConfiguratorDefinition,
  CsticDefinition,
  CsticToSet,
  CsticValueMapping,
  OptionDefinition,
  ResolvedCsticDefinition,
  StepDefinition,
  SubstepDefinition,
} from "~/Configurators/common/v2/StepDefinition";
import { RailsDefinition } from "~/Configurators/Rail/v2/configuration/classic/RailConfiguratorDefinition";
import { CarriagesDefinition } from "~/Configurators/Carriage/v2/configuration/CarriageConfiguratorDefinition";
import { LCValueResolver, type SerializableDynamicValue } from "~/Configurators/common/v2/types";
import { isNonNull, toMapFn } from "~/utils/reducers";
import { CarriagesDefaultValues } from "~/Configurators/Carriage/v2/configuration/CarriagesDefaultValues";
import { RailsDefaultValues } from "~/Configurators/Rail/v2/configuration/classic/RailsDefaultValues";
import { ImperialShaftConfiguratorDefinition } from "~/Configurators/Shaft/v2/configuration/ImperialShaftConfiguratorDefinition";
import { ImperialShaftDefaultValues } from "~/Configurators/Shaft/v2/configuration/ImperialShaftDefaultValues";
import { MetricShaftConfiguratorDefinition } from "~/Configurators/Shaft/v2/configuration/MetricShaftConfiguratorDefinition";
import { MetricShaftDefaultValues } from "~/Configurators/Shaft/v2/configuration/MetricShaftDefaultValues";
import {
  CARRIAGE_CONFIGURATOR_PRODUCT_CODE,
  INCH_SHAFT_CONFIGURATOR_PRODUCT_CODE,
  METRIC_SHAFT_CONFIGURATOR_PRODUCT_CODE,
  RAIL_CONFIGURATOR_PRODUCT_CODE,
  TKDM_RAIL_CONFIGURATOR_PRODUCT_CODE,
  TKLT_RAIL_CONFIGURATOR_PRODUCT_CODE,
  UNIT_CONFIGURATOR_PRODUCT_CODE,
} from "~/Configurators/constants";
import { TKDMRailsDefaultValues } from "~/Configurators/Rail/v2/configuration/tkdm/TKDMRailsDefaultValues";
import { TKDMRailsDefinition } from "~/Configurators/Rail/v2/configuration/tkdm/TKDMRailConfiguratorDefinition";
import { TKLTRailsDefaultValues } from "~/Configurators/Rail/v2/configuration/tklt/TKLTRailsDefaultValues";
import { TKLTRailsDefinition } from "~/Configurators/Rail/v2/configuration/tklt/TKLTRailConfiguratorDefinition";
import { UnitConfiguratorDefinition } from "~/Configurators/Linear/v2/configuration/UnitConfiguratorDefinition";
import { UnitDefaultValues } from "~/Configurators/Linear/v2/configuration/UnitDefaultValues";

const DEFINITION_MAP: Record<string, ConfiguratorDefinition> = {
  [RAIL_CONFIGURATOR_PRODUCT_CODE]: RailsDefinition,
  [TKDM_RAIL_CONFIGURATOR_PRODUCT_CODE]: TKDMRailsDefinition,
  [TKLT_RAIL_CONFIGURATOR_PRODUCT_CODE]: TKLTRailsDefinition,
  [CARRIAGE_CONFIGURATOR_PRODUCT_CODE]: CarriagesDefinition,
  [UNIT_CONFIGURATOR_PRODUCT_CODE]: UnitConfiguratorDefinition,
  [METRIC_SHAFT_CONFIGURATOR_PRODUCT_CODE]: MetricShaftConfiguratorDefinition,
  [INCH_SHAFT_CONFIGURATOR_PRODUCT_CODE]: ImperialShaftConfiguratorDefinition,
};

const DEFAULT_VALUES_MAP: Record<string, Record<string, SerializableDynamicValue<string>>> = {
  [RAIL_CONFIGURATOR_PRODUCT_CODE]: RailsDefaultValues,
  [TKDM_RAIL_CONFIGURATOR_PRODUCT_CODE]: TKDMRailsDefaultValues,
  [TKLT_RAIL_CONFIGURATOR_PRODUCT_CODE]: TKLTRailsDefaultValues,
  [CARRIAGE_CONFIGURATOR_PRODUCT_CODE]: CarriagesDefaultValues,
  [UNIT_CONFIGURATOR_PRODUCT_CODE]: UnitDefaultValues,
  [METRIC_SHAFT_CONFIGURATOR_PRODUCT_CODE]: MetricShaftDefaultValues,
  [INCH_SHAFT_CONFIGURATOR_PRODUCT_CODE]: ImperialShaftDefaultValues,
};

export function applyConfigurationFromDefinition(
  configuration: LinearConfiguration | LinearConfigurationUpdate,
  configuratorType: ConfiguratorType,
  mainKmat: Kmat,
  subKmats: Kmat[],
) {
  const productCode = configuration.productCode;
  // WIP on replacing BE configuration with ConfiguratorDefinition
  const definition: ConfiguratorDefinition | null = DEFINITION_MAP[productCode] ?? null;
  const defaultValues: Record<string, SerializableDynamicValue<string>> | null =
    DEFAULT_VALUES_MAP[productCode] ?? null;

  if (definition && defaultValues) {
    const resolver = new LCValueResolver({
      mainKmat: mainKmat,
      subKmats,
    });
    definition.pages.forEach((page, index) => {
      configuration.steps[index] = convertToStep(page, defaultValues, resolver);
      page.substeps.forEach((substep) => {
        configuration.substeps[substep.type] = convertToSubstep(substep, resolver);
      });
    });
    configuration.description = resolver.resolveNullableValue(definition.description) ?? "";
    configuration.mediaUrl = resolver.resolveNullableValue(definition.mediaUrl) ?? "";
  }
}

function convertToStep(
  step: StepDefinition,
  defaultValues: Record<string, SerializableDynamicValue<string>>,
  resolver: LCValueResolver,
): LinearStep {
  return {
    ...step,
    substeps: step.substeps.map((substep) => substep.type),
    defaultValues: getDefaultValues(step.substeps, defaultValues, resolver),
    active: step.enabled,
    showResetConfirmation: step.resetRequiresConfirmation,
  };
}

function convertToSubstep(substep: SubstepDefinition, resolver: LCValueResolver): LinearSubstep {
  const filterOption = (tile: OptionDefinition) => {
    if (tile.visible === undefined) {
      return true;
    }
    return resolver.resolveNullableValue(tile.visible) ?? false;
  };

  const mapOption = (tile: OptionDefinition) => convertToTile(tile, resolver);

  return {
    ...substep,
    tilesPerRow: substep.tilesPerRow ?? 1,
    name: resolver.resolveNullableValue(substep.name) ?? "",
    description: resolver.resolveNullableValue(substep.description) ?? "",
    info: resolver.resolveNullableValue(substep.info) ?? "",
    footerInfo: substep?.footerInfo?.map((item) => resolver.resolveNullableValue(item)).filter(isNonNull) ?? [],
    extraTexts: substep.extraTexts ?? {},
    tiles: (substep.tiles ?? []).filter(filterOption).map(mapOption),
    groupedTiles: (substep.groupedTiles ?? [])
      .filter((group) => resolver.resolveNullableValue(group.visible) ?? false)
      .map((group) => ({
        ...group,
        name: group.name ?? "",
        description: group.description ?? "",
        info: group.info ?? "",
        tiles: group.tiles.filter(filterOption).map(mapOption),
      })),
    selectedTile: null,
    value: null,
    additionalValidations: {},
    additionalData: {},
    isVisible: resolver.resolveNullableValue(substep.visible) ?? false,
    inputCstics: resolveMap(resolver, substep.inputCstics ?? {}, resolveCsticDefinition),
    helperCstics: resolveMap(resolver, substep.helperCstics ?? {}, resolveCsticDefinition),
    extraConfig: substep.extraConfig ?? {},
  };
}

function convertToTile(option: OptionDefinition, resolver: LCValueResolver): SubstepTile {
  const toRecord = (cstics: CsticToSet[]) => {
    return toMapFn(
      cstics,
      ({ csticName }) => csticName,
      ({ value }) => value,
    );
  };

  const cstics = resolver.resolveList(option.cstics);
  const csticsToEnable = cstics.filter((cstic) => !!cstic.value);

  return {
    name: option.name ?? "",
    description: option.description ?? "",
    imageUrl: option.imageUrl ?? "",
    info: resolver.resolveNullableValue(option.info) ?? "",

    cstics: toRecord(cstics),
    csticsToEnable: option.overrideCsticsToEnable ? {} : toRecord(csticsToEnable),

    isDisabledExternally: resolver.resolveNullableValue(option.disabled) ?? false,
  };
}

function getDefaultValues(
  substeps: SubstepDefinition[],
  defaultValuesMapping: Record<string, SerializableDynamicValue<string>>,
  resolver: LCValueResolver,
): CsticValueMapping {
  const defaultValues: CsticValueMapping = {};
  (substeps ?? [])
    .flatMap((substep) => substep.csticsToReset ?? [])
    .forEach((csticName) => {
      const resolvableValue = defaultValuesMapping[csticName];
      if (resolvableValue !== undefined) {
        const resolvedValue = resolver.resolveDefaultValue(resolvableValue);
        if (resolvedValue !== null) {
          defaultValues[csticName] = resolvedValue;
        }
      } else {
        defaultValues[csticName] = null;
      }
    });
  return defaultValues;
}

function resolveMap<T, V>(
  resolver: LCValueResolver,
  map: Record<string, T>,
  mappingFunc: (value: T, resolver: LCValueResolver) => V,
): Record<string, V> {
  return Object.fromEntries(Object.entries(map).map(([key, value]) => [key, mappingFunc(value, resolver)]));
}

function resolveCsticDefinition(value: CsticDefinition, resolver: LCValueResolver): ResolvedCsticDefinition {
  return {
    name: value.name,
    disabled: resolver.resolveNullableValue(value.disabled) ?? false,
    visible: resolver.resolveNullableValue(value.visible) ?? true,
    required: resolver.resolveNullableValue(value.required) ?? false,
  };
}
